import { Box } from '@mui/material';
import type { SectionLayout } from '@/blocks/domain/types/BlockContent/HeaderContent';

/** The five layouts in the design's order, with their column ratios for the glyph. */
export const CANVAS_SECTION_LAYOUTS: ReadonlyArray<{ layout: SectionLayout; label: string; ratios: number[] }> = [
  { layout: '1', label: '1 column', ratios: [1] },
  { layout: '2', label: '2 columns', ratios: [1, 1] },
  { layout: '3', label: '3 columns', ratios: [1, 1, 1] },
  { layout: '1:2', label: '1/3 + 2/3', ratios: [1, 2] },
  { layout: '2:1', label: '2/3 + 1/3', ratios: [2, 1] }
];

interface Props {
  layout: SectionLayout;
  sectionName: string;
  onChange: (layout: SectionLayout) => void;
  testId: string;
}

/**
 * The section tab's column switcher: five toggle buttons (one pressed), each drawn as its column
 * split. Changing the layout folds the elements of a removed column into the last one that remains.
 */
export function CanvasSectionLayoutSwitcher({ layout, sectionName, onChange, testId }: Props) {
  return (
    <Box
      role="group"
      aria-label={`Columns - ${sectionName}`}
      data-testid={testId}
      sx={{
        display: 'flex',
        gap: '2px',
        p: '2px',
        bgcolor: 'var(--surface-neutral-container-secondary)',
        border: 'var(--border-width-xs) dashed var(--border-focus)',
        borderRadius: 'var(--border-radius-none)'
      }}
    >
      {CANVAS_SECTION_LAYOUTS.map((option) => {
        const pressed = option.layout === layout;
        return (
          <Box
            key={option.layout}
            component="button"
            type="button"
            aria-label={option.label}
            aria-pressed={pressed}
            title={option.label}
            data-testid={`${testId}:${option.layout}`}
            onClick={(event) => {
              event.stopPropagation();
              if (!pressed) onChange(option.layout);
            }}
            sx={{
              width: 22,
              height: 14,
              p: '2px',
              border: 0,
              borderRadius: '2px',
              display: 'flex',
              gap: '1px',
              cursor: 'pointer',
              bgcolor: pressed ? 'var(--border-focus)' : 'transparent',
              '&:focus-visible': { outline: 'var(--border-width-sm) solid var(--border-focus)', outlineOffset: 1 }
            }}
          >
            {option.ratios.map((ratio, index) => (
              <Box
                key={index}
                aria-hidden
                sx={{
                  flex: ratio,
                  borderRadius: '1px',
                  bgcolor: pressed ? 'var(--text-on-color-body)' : 'var(--border-focus)',
                  opacity: pressed ? 1 : 0.55
                }}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
